import { useEffect, useState, useMemo } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ScrollElement from '../../../global/Elements/ScrollElement';
import { searchContext, userAppContext } from '../../../Context/Context.jsx';
import GeneContent from '../../../Components/GeneContent/GeneContent.jsx';
import { scroller } from 'react-scroll';

import ManageGrid from './components/table/ManageGrid';
import ManageGraph from './components/table/ManageGraph.jsx';

import SummaryGDAGraphs from '../../../Components/Graphs/SummaryGDA/SummaryGDAGraphs.jsx';
import SummaryVDAGraphs from '../../../Components/Graphs/SummaryVDA/SummaryVDAGraphs.jsx';

import { useNavigate } from 'react-router-dom';
import MappingResults from '../../../Components/Mapping/MappingResults.jsx';
import { SearchContext, UserContext } from '../../../types.js';
import SharedButton from '../../../global/Button/Button';
import { getViewToggleText } from './helpers/graphHelpers';
import { getTabList, getId, getName, getTitle } from './helpers/tableHelpers';
import DDAGraphs from '../../../Components/Graphs/DDA/DDAGraphs.jsx';
import { trackBrowseTabClick } from '../../../utils/analytics';

function SearchResults({ state, dispatch }) {
  const navigate = useNavigate();
  const {
    view,
    source,
    tab,
    searchItems,
    setSearchItems,
    geneContent,
    setGeneContent,
    mappingContent,
    selectedInfoItem,
  } = searchContext() as unknown as SearchContext;
  const { restrictGraphDisplay, isAuthenticated, groups } = userAppContext() as unknown as UserContext;
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedView, setSelectedView] = useState('tables');
  const [defaultFilterValue, _setDefaultFilterValue] = useState([]);

  const isTrial = groups?.includes('ROLE_TRIAL');

  const handleViewChange = () => {
    setSelectedView(prevcurrentView => (prevcurrentView === 'tables' ? 'graphs' : 'tables'));
  };

  const tabList = useMemo(() => {
    return getTabList(view as string, tab as string);
  }, [view, tab]);

  const id = getId(selectedInfoItem);
  const name = getName(selectedInfoItem);
  const title = getTitle(selectedInfoItem);

  const handleTabSelect = index => {
    setTabIndex(index);
    const newTab = tabList[index].key;
    const tabLabel = tabList[index].label;

    trackBrowseTabClick(tabLabel);

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('tab', newTab);

    navigate({ pathname: window.location.pathname, search: `?${currentParams.toString()}` });
  };

  useEffect(() => {
    // Update nav component state based on app state
    const newIndex = tabList.findIndex(t => t.key === tab);
    setTabIndex(newIndex);
  }, [tab, tabList]);

  // Table/Graph button
  // Button is only shown on Association views, and not CHEMICALS
  // const showManageGraph = view !== tab && ['GDA', 'VDA', 'DDA'].includes(tab);
  const showManageGraph = view !== 'ALL' && view !== 'CHEMICALS';

  // Grid view items
  const urlParams = new URLSearchParams(window.location.search);
  const isEvidenceParam = urlParams.get('isEvidence');
  
  let showSummary = true;
  let showEvidence = true;
  
  if (isEvidenceParam === 'false') {
    showEvidence = false;
  } else if (isEvidenceParam === 'true') {
    showSummary = false;
  } else {
    if (tab === 'DDA') showEvidence = false;
    if (view === 'ALL' && !['GDA', 'VDA'].includes(tab as string)) showEvidence = false;
  }

  // Graph view items
  const graphMap = {
    GDA: SummaryGDAGraphs,
    VDA: SummaryVDAGraphs,
    DDA: DDAGraphs,
  };

  useEffect(() => {
    // page should return to table view when last searchItem is removed
    if (searchItems?.length === 0) setSelectedView('tables');
  }, [searchItems]);

  // scroll to ref animation
  const scrollToRef = ref => {
    scroller.scrollTo(ref, {
      duration: 750,
      // delay: 50,
      // smooth: 'easeInOutQuad',
      smooth: true,
    });
  };

  useEffect(() => {
    if (geneContent!.isActive) {
      scrollToRef('Extra-Content');
    }
    if (mappingContent!.isActive) {
      scrollToRef('Mapping');
    }
  }, [geneContent!.isActive, mappingContent!.isActive]);

  // Auto-scroll to Evidence table if URL has only Evidence filters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let hasEvidenceFilters = false;
    let hasSummaryFilters = false;

    for (const [key] of params.entries()) {
      if (key.startsWith('filters-evidence-') || key.startsWith('filters[evidence][')) {
        hasEvidenceFilters = true;
      }
      if (key.startsWith('filters-summary-') || key.startsWith('filters[summary][')) {
        hasSummaryFilters = true;
      }
    }

    if (hasEvidenceFilters && !hasSummaryFilters && showEvidence) {
      // Small delay to ensure the component is mounted
      setTimeout(() => {
        scrollToRef('Evidence');
      }, 300);
    }
  }, [showEvidence]); 

  return (
    <>
      {!isAuthenticated && view !== 'ALL' && (
        <div className="info-container p-2 text-center">
          <p style={{ fontSize: '18px', color: 'rgb(190,0,128)' }}>
            As an unregistered user you are viewing only 10 results ordered by score for each query. To view
            more, register for an account. Learn more about our <a href="/plans">plans here</a>.
          </p>
        </div>
      )}
      {isTrial && (
        <div className="info-container p-2 text-center">
          <p style={{ fontSize: '18px', color: 'rgb(190,0,128)', paddingTop: '10px' }}>
            Only the top 30 results are shown. <a href="/academic-plans" style={{ color: "#0d1661" }}>Upgrade your account </a> to see all results. <a href="/plans">View our plans</a>.
           </p>
        </div>
      )}
      <div
        className={`search-results-container ${selectedView === 'tables' ? 'flex' : ''}`}
        data-testid="search-results"
      >
        <div className="pt-5 pr-1 pl-1 md:p-5" style={{ flex: '1', height: '100%' }}>
          <Tabs
            className="react-tabs superposed-content"
            selectedIndex={tabIndex}
            onSelect={index => handleTabSelect(index)}
          >
            <div className="d-flex justify-content-between align-items-center">
              <TabList className="react-tabs__tab-list">
                {tabList.map(t => (
                  <Tab key={t.key} className={'react-tabs__tab bg-white'}>
                    {/* <i className="fa fa-dot-circle-o" /> */}
                    {t.label}
                  </Tab>
                ))}
              </TabList>
              {showManageGraph && !restrictGraphDisplay && (
                <SharedButton size="md" onClick={() => handleViewChange()}>
                  View {getViewToggleText(selectedView)}
                </SharedButton>
              )}
            </div>

            <div>
              {tabList.map(t => (
                <TabPanel key={t.key} className="tab-pane">
                  <>
                    <ScrollElement name="Summary">
                      {selectedView === 'tables' && showSummary && (
                        <ManageGrid
                          source={source}
                          view={view}
                          tab={tab}
                          searchItems={searchItems}
                          setSearchItems={setSearchItems}
                          isEvidence={false}
                          isMapping={false}
                          isInfoSidebarOpen={isInfoSidebarOpen}
                          setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                          defaultFilterValue={defaultFilterValue}
                          dispatch={dispatch}
                        />
                      )}
                      {selectedView === 'graphs' && showManageGraph && (
                        <ManageGraph
                          source={source}
                          view={view}
                          tab={tab}
                          searchItems={searchItems}
                          setSearchItems={setSearchItems}
                          sectionTitle={t.label}
                          names={searchItems}
                          title={view}
                          Graph={graphMap[t.key]}
                          setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                        />
                      )}
                    </ScrollElement>
                    <ScrollElement name="Evidence">
                      {selectedView === 'tables' && showEvidence && (
                        <ManageGrid
                          view={view}
                          source={source}
                          tab={tab}
                          searchItems={searchItems}
                          setSearchItems={setSearchItems}
                          isEvidence={true}
                          isMapping={false}
                          setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                          isInfoSidebarOpen={isInfoSidebarOpen}
                          defaultFilterValue={defaultFilterValue}
                          dispatch={dispatch}
                        />
                      )}
                    </ScrollElement>{' '}
                    <ScrollElement name="Mapping">
                      {selectedView === 'tables' && mappingContent?.isActive && (
                        <MappingResults mappingContent={mappingContent} dispatch={dispatch} />
                      )}
                    </ScrollElement>
                  </>
                </TabPanel>
              ))}
            </div>
          </Tabs>
          <div className="gene-content-container">
            <ScrollElement name="Extra-Content" />
            {geneContent!.isActive && (
              <GeneContent
                isActive={geneContent!.isActive}
                geneInfo={geneContent!.geneInfo}
                setGeneContent={setGeneContent}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
export default SearchResults;
